import { assignHomeworkInputSchema, clientToolOfHomework, homeworkDefaultTitleKey } from '@cc/schema';
import { toolLabel } from '../../components/clientTools/labels';
import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Drawer,
  Input,
  Radio,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { startOfDay } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ClientName,
  ClientNameInText,
  NAME_TOKEN,
} from '../../components/clientNames';
import { DateFnsDatePicker } from '../../components/Pickers';
import { SubmitFooter } from '../../components/SubmitFooter';
import { usePortalLanguage } from '../../components/WhatsNew/useWhatsNew';
import { ALL_CATEGORIES, filterByCategory, keepIfInCategory } from '../worksheets/categoryFilter';
import { worksheetLanguageArg } from '../worksheets/model';
import { WorksheetCategorySelect } from '../worksheets/WorksheetCategorySelect';
import { LEARN_ARTICLE_IDS } from './learnArticles';
import {
  ANY_WORKOUT,
  ASSESSMENT_TARGETS,
  ASSIGNABLE_KINDS,
  HOMEWORK_ACTIVITY_MINUTES_MAX,
  HOMEWORK_ACTIVITY_TIMES_MAX,
  REMINDER_POLICIES,
  TOOL_TARGETS,
  WORKOUT_TYPES,
  toolTargetGroups,
  activityTargetOf,
  activityTargetProblem,
  browserZone,
  freshActivityTarget,
  defaultDueTime,
  defaultTitleOf,
  defaultInstructionsPlaceholder,
  defaultTitlePlaceholder,
  targetTypeOf,
  videoTargetOf,
  type ActivityTargetDraft,
  type AssignableKind,
  type AssignDefaults,
  type AssignPreset,
} from './model';
import {
  RepeatEditor,
  freshRepeat,
  repeatOfPreset,
  repeatProblem,
  scheduleOfRepeat,
  type RepeatDraft,
} from './RepeatEditor';
import { useHomeworkCommand } from './useHomeworkCommand';
import { VideoTargetInput } from './VideoTargetInput';
import { usePickerLocale } from './usePickerLocale';
import { useMyHiddenTools } from '../../hooks/useMyHiddenTools';

/**
 * E34 · AssignHomework — "Client Hub — Homework — Assign (drawer)" (cc-homework, chapter "Homework —
 * Assign & follow (portal, E34)"). The therapist gives one piece of homework: a kind, what it points
 * at, a title and instructions, and ONE deadline shape — a due instant OR a repeat rule (the E05
 * reminder editor's rule, `components/EsCardReminders/rrule`) — plus how the notifier nags.
 *
 * Behavior lines implemented here:
 *   · gesture: Assign → AssignHomework (i18n homework.assign.*);
 *   · the Target picker is scoped by Kind (tools · the instruments the practice may assign · the Learn
 *     articles · a URL; a task has none) — the block-1b kinds, see `ASSIGNABLE_KINDS`;
 *   · Due at and Schedule are mutually exclusive (one Radio chooses the shape);
 *   · Q-paper-3 B: a Tool shows the switch "May do it on paper" (off by default; sent as `paperAllowed`); a Worksheet
 *     shows, under its target, whether the template allows paper — automatic, no switch (Q-paper-2 A);
 *   · Q-due-1 A: the one-off "Due at" picker pre-selects 18:00 once a date is picked;
 *   · E43 (Q-e43g1-8 A): a TASK may state an activity target — workout type (or any), minutes each, times a week —
 *     sent as `activityTarget` (null when the section is off); the evidence matcher completes it from the phone's days;
 *   · nothing is sent to the client from here — the notifier (block 1d) tells them;
 *   · opened from the Practice — Homework board with several clients: one AssignHomework PER CLIENT,
 *     no group entity (the founder's Q-e34-5); a refusal for one client shows against that name and
 *     the others are still assigned.
 *
 * The same drawer opens from the client hub's Homework tab and the session form's panel.
 */
export type AssignTarget = {
  subject: string;
  name: string;
  /**
   * Can the app this client last used show homework? (the founder's Q-b1d-1′ A — `homeworkAppReady`
   * on the hub's client read and the board's rows). False: the drawer says so; assigning still
   * records. Undefined: not known here — nothing is said.
   */
  appReady?: boolean;
  /**
   * Known when the drawer opens (Q-mt-2 A): the client is not connected to the practice, or has
   * switched sharing off (the roster's `isPracticeClient` is false). The drawer says so at the TOP and
   * does not assign to them; with nobody left to assign to, Assign is disabled.
   */
  notConnected?: boolean;
  /**
   * The client's `Client.lng` (Q-fix-1 C): a video's Learn list then offers only that language's catalogue.
   * Undefined / empty: the therapist's portal language (Q-cf-2). The drawer reads the FIRST client's.
   */
  lng?: string | null;
};

export type AssignOutcome = {
  subject: string;
  name: string;
  ok: boolean;
  assignmentId?: string;
  message?: string;
};

type Props = {
  open: boolean;
  clients: AssignTarget[];
  /** The client's own IANA zone (one client) — a repeat rule runs in it; else the browser's. */
  clientTimeZone?: string | null;
  /**
   * Q-mt-3 A: are client names readable? A page that lists several clients (the Practice — Homework
   * board) passes ITS state — hidden by default there; the single-client hub, whose header names the
   * client already, leaves it true.
   */
  namesVisible?: boolean;
  /** E35 — opened from the Assessments tab on one instrument: the kind and target start there (a fresh form otherwise). */
  preset?: AssignPreset;
  onClose: () => void;
  /** After at least one assignment landed (the caller refetches its list). */
  onAssigned: (outcomes: AssignOutcome[]) => void;
};

export type { AssignDefaults, AssignPreset } from './model';

type DeadlineShape = 'due' | 'repeat';
const dueOf = (d: AssignDefaults | undefined) => (d?.deadline?.shape === 'due' ? d.deadline : undefined);
const repeatDefaultsOf = (d: AssignDefaults | undefined) => (d?.deadline?.shape === 'repeat' ? d.deadline : undefined);
export const AssignHomeworkDrawer = ({
  open,
  clients,
  clientTimeZone,
  namesVisible = true,
  preset,
  onClose,
  onAssigned,
}: Props) => {
  const t = useTranslate();
  const pickerLocale = usePickerLocale();
  const { message } = AntdApp.useApp();
  const { run } = useHomeworkCommand();

  const [kind, setKind] = useState<AssignableKind>('tool');
  // Q-ws-lang-2 A: the worksheet picker lists the portal's language; the switch lists every language
  const [allLanguages, setAllLanguages] = useState(false);
  const portalLanguage = usePortalLanguage();
  const [target, setTarget] = useState<string | undefined>();
  // E37: a diary-card step may name a starting template; the phone opens "Set up my card" with it preselected
  const [diaryTemplate, setDiaryTemplate] = useState<string | undefined>();
  // P039: the worksheet Category filter — opens on All every time, nothing remembered
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const [link, setLink] = useState('');
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [shape, setShape] = useState<DeadlineShape>('due');
  const [dueAt, setDueAt] = useState<Date | null>(null);
  const [repeat, setRepeat] = useState<RepeatDraft>(freshRepeat);
  const [policy, setPolicy] = useState<string>('default');
  // §1.8: the chosen ref (a behavior, a change) of a tool preset; and whether the therapist has typed the title / instructions
  const [ref, setRef] = useState<string | undefined>();
  const [titleTouched, setTitleTouched] = useState(false);
  const presetRef = useRef(preset);
  presetRef.current = preset;
  // Q-paper-3 B: a tool's "May do it on paper" — OFF by default, the therapist turns it on per assignment
  const [paperAllowed, setPaperAllowed] = useState(false);
  // E43: a task's optional activity target (off by default; a draft of strings the therapist types)
  const [activity, setActivity] = useState<ActivityTargetDraft>(freshActivityTarget);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [outcomes, setOutcomes] = useState<AssignOutcome[]>([]);
  // Q-mt-2 A: a submit error clears on the next edit or press — a refusal is stale once the form changed
  const [edited, setEdited] = useState(false);

  /**
   * §1.8 · a tool target's defaults: the title and instructions only while the therapist has not typed them (`force` on open);
   * the deadline shape, the repeat and its count, the policy and the ref always (they belong to the target).
   */
  const applyDefaults = (d: AssignDefaults | undefined, force: boolean, keepTitle?: string) => {
    if (!d) return;
    const refValue = d.ref?.value && d.ref.value !== d.ref.any?.value ? d.ref.value : undefined;
    const refLabel = d.ref?.options.find((o) => o.value === refValue)?.label ?? null;
    const nextTitle = defaultTitleOf(d, refLabel);
    if (nextTitle !== null && (force || !titleTouched)) setTitle(nextTitle || keepTitle || '');
    if (d.reminderPolicy) setPolicy(d.reminderPolicy);
    if (d.deadline?.shape === 'repeat') {
      setShape('repeat');
      setRepeat(repeatOfPreset(d.deadline));
    } else if (d.deadline?.shape === 'due') {
      setShape('due');
      setDueAt(null);
      setRepeat(freshRepeat());
    }
    setRef(d.ref ? (d.ref.value ?? d.ref.any?.value) : undefined);
  };

  // a fresh form each time the drawer opens — it is a new decision
  useEffect(() => {
    if (!open) return;
    setKind(preset?.kind ?? 'tool');
    setAllLanguages(false);
    setCategory(ALL_CATEGORIES);
    setTarget(preset?.target);
    setDiaryTemplate(undefined);
    setLink('');
    setTitle(preset?.title ?? '');
    setInstructions('');
    setShape('due');
    setDueAt(null);
    setRepeat(freshRepeat());
    setPolicy('default');
    setRef(undefined);
    setTitleTouched(false);
    if (preset?.target) applyDefaults(preset.defaults?.[preset.target], true, preset.title ?? '');
    setPaperAllowed(false);
    setActivity(freshActivityTarget());
    setProblem(null);
    setOutcomes([]);
    setEdited(false);
  }, [open, preset?.kind, preset?.target, preset?.title]);

  // …the next edit: any field of the form
  useEffect(() => {
    setProblem(null);
    setEdited(true);
  }, [kind, target, diaryTemplate, link, title, instructions, shape, dueAt, repeat, policy, paperAllowed, activity, ref]);

  /**
   * E57 · My tools trims the PICKER (the board: "a hidden tool leaves the row and the pickers"): a tool target, the
   * instruments or the worksheets the therapist hid are not offered. The Homework tool's own kinds (reading, task, link,
   * video) stay — this drawer IS that tool, opened on purpose. A preset (opened from a tool's own tab) is always kept.
   */
  const { hiddenTools } = useMyHiddenTools(open);
  const hiddenSet = useMemo(() => new Set(hiddenTools), [hiddenTools]);
  const kindHidden = (k: AssignableKind): boolean =>
    k !== preset?.kind &&
    ((k === 'assessment' && hiddenSet.has('assessments')) || (k === 'worksheet' && hiddenSet.has('worksheets')));
  const toolTargetHidden = (x: string): boolean => {
    if (x === preset?.target || preset?.targets?.includes(x)) return false;
    const tool = clientToolOfHomework('tool', { type: x });
    return !!tool && hiddenSet.has(tool);
  };

  const targetDefaults = kind === 'tool' && target ? preset?.defaults?.[target] : undefined;
  const dueDefaults = dueOf(targetDefaults);
  const repeatDefaults = repeatDefaultsOf(targetDefaults);
  const dueDefaultTime = useMemo(() => {
    const d = defaultDueTime();
    if (dueDefaults?.defaultHour !== undefined) d.setHours(dueDefaults.defaultHour, 0, 0, 0);
    return d;
  }, [dueDefaults?.defaultHour]);

  const blocked = clients.filter((c) => c.notConnected);
  const assignable = clients.filter((c) => !c.notConnected);
  const nobodyToAssign = clients.length > 0 && assignable.length === 0;

  // a DUE instant is picked on the therapist's own clock (the picker is the browser's); a REPEAT rule
  // runs in the client's zone when it is known (the E05 editor's rule), else in the therapist's
  const zone =
    clients.length === 1 && clientTimeZone ? clientTimeZone : browserZone();
  const zoneNote =
    shape === 'repeat' && clients.length === 1 && clientTimeZone
      ? t('homework.assign.clientZone', { zone })
      : t('homework.assign.yourZone', {
          zone: shape === 'repeat' ? zone : browserZone(),
        });

  // block 2a — the worksheet picker reads the library (the practice's own + CopingCard's; retired hidden)
  const { data: worksheetData } = useCustom<
    Array<{
      templateId: string
      name: string
      category: string
      owner: string
      paperEligible: boolean
    }>
  >({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: open && kind === 'worksheet' },
    meta: {
      operation: 'worksheetTemplates',
      fields: ['templateId', 'name', 'category', 'owner', 'paperEligible'],
      variables: {
        language: {
          name: 'language',
          type: 'String',
          value: worksheetLanguageArg(allLanguages, undefined, portalLanguage),
        },
      },
    },
  });
  const worksheets = useMemo(() => worksheetData?.data ?? [], [worksheetData]);

  // E37 — the diary card's template catalogue, for the optional "starting template" of an assigned diary card
  const { data: diaryTemplateData } = useCustom<Array<{ templateId: string; name: string }>>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: open && kind === 'tool' && target === 'diaryCard', retry: false },
    meta: {
      operation: 'diaryCardTemplates',
      fields: ['templateId', 'name'],
      variables: {
        locale: { name: 'locale', type: 'String', value: portalLanguage === 'ru' ? 'ru' : 'en' },
      },
    },
  });
  const diaryTemplates = useMemo(() => diaryTemplateData?.data ?? [], [diaryTemplateData]);

  type TargetOption = { value: string; label: string; group?: string };
  const targetOptions = useMemo((): Array<TargetOption | { label: string; options: TargetOption[] }> => {
    switch (kind) {
      case 'worksheet':
        return filterByCategory(worksheets, category).map((w) => ({
          value: w.templateId,
          label: `${w.name} · ${t(`worksheets.category.${w.category}`)}${
            w.owner === 'library'
              ? ` · ${t('worksheets.library.owners.library')}`
              : ''
          }`,
        }));
      case 'tool':
        // N-11 (founder, 2026-10-22): grouped under the tool's standard name; each option carries its group so the search finds a
        // tool's steps by the tool's name too
        return toolTargetGroups(
          TOOL_TARGETS.filter((x) => !toolTargetHidden(x) && (!preset?.targets || preset.targets.includes(x))),
        ).map((g) => {
          const group = g.tool ? toolLabel(t, g.tool) : t('homework.kind.tool');
          return {
            label: group,
            options: g.targets.map((x) => ({ value: x, label: t(`homework.target.tool.${x}`), group })),
          };
        });
      case 'assessment':
        return ASSESSMENT_TARGETS.map((x) => ({
          value: x,
          label: t(`homework.target.assessment.${x}`),
        }));
      case 'reading':
        return LEARN_ARTICLE_IDS.map((x) => ({
          value: x,
          label: t(`homework.articles.${x}`),
        }));
      default:
        return [];
    }
  }, [kind, t, worksheets, category, hiddenSet, preset?.targets, preset?.target]);

  /**
   * Q-ru-3 A · while the Title is empty its placeholder is the picked tool's default title in the portal's language (what the client
   * will get); the value stays empty and is sent as `title: null`. No tool picked / no default: the generic example.
   */
  const toolDefaultsNow = kind === 'tool' && target ? preset?.defaults?.[target] : undefined;
  const placeholderName =
    toolDefaultsNow?.ref && ref && ref !== toolDefaultsNow.ref.any?.value
      ? toolDefaultsNow.ref.options.find((o) => o.value === ref)?.label ?? null
      : null;
  const titlePlaceholder =
    (kind === 'tool' && target ? defaultTitlePlaceholder(kind, target, placeholderName, t) : null) ??
    t('homework.assign.titlePlaceholder');
  // "Default instructions: A": the picked tool's own default; a tool with none gets no thought-test example
  const instructionsPlaceholder =
    (kind === 'tool' && target ? defaultInstructionsPlaceholder(kind, target, t) : null) ??
    (kind === 'tool' && target && target !== 'thoughtTest' ? undefined : t('homework.assign.instructionsPlaceholder'));

  /** The input the both-ends codec and the API take — or the first thing still missing. */
  const compose = ():
    | { input: Record<string, unknown> }
    | { missing: string } => {
    if (!title.trim()) return { missing: 'homework.assign.required.title' };
    let targetValue: Record<string, unknown> | null = null;
    if (kind === 'link') {
      if (!/^https:\/\/\S+$/.test(link.trim()))
        return { missing: 'homework.assign.required.link' };
      targetValue = { type: 'url', ref: link.trim() };
    } else if (kind === 'video') {
      targetValue = videoTargetOf(link);
      if (!targetValue) return { missing: 'homework.assign.required.video' };
    } else if (kind !== 'task') {
      if (!target) return { missing: 'homework.assign.required.target' };
      const type = targetTypeOf(kind, target) as string;
      // §1.8: a tool's ref (a behavior, a change) rides with the target - unless it is the "any" choice (no ref sent)
      const refDef = kind === 'tool' ? preset?.defaults?.[target]?.ref : undefined;
      const refSent = refDef && ref && ref !== refDef.any?.value ? ref : undefined;
      if (refDef && !refDef.any && !refSent) return { missing: refDef.requiredMessage ?? 'homework.assign.required.target' };
      targetValue =
        kind === 'tool'
          ? type === 'diaryCard' && diaryTemplate
            ? { type, ref: diaryTemplate }
            : refSent
            ? { type, ref: refSent }
            : { type }
          : { type, ref: target };
    }
    // Q-ru-3 A (2026-10-24): a tool's DEFAULT title left as it is is not sent — the client, the push and the e-mail render it in
    // their own language; the behavior's name rides along for a default that quotes it (functional analysis). Edited = sent.
    const toolDefaults = kind === 'tool' && target ? preset?.defaults?.[target] : undefined;
    const refLabelNow =
      toolDefaults?.ref && ref && ref !== toolDefaults.ref.any?.value
        ? toolDefaults.ref.options.find((o) => o.value === ref)?.label ?? null
        : null;
    const defaultTitleNow = defaultTitleOf(toolDefaults, refLabelNow);
    const titleIsDefault =
      defaultTitleNow !== null &&
      title.trim() === defaultTitleNow.trim() &&
      homeworkDefaultTitleKey(kind, targetValue as { type?: string } | null) !== null;
    const titleFields = titleIsDefault
      ? { title: null, ...(typeof toolDefaults?.title === 'function' && refLabelNow ? { titleName: refLabelNow } : {}) }
      : { title: title.trim() };
    // E43: the activity target is a task's alone; off (or another kind) sends null
    const activityProblem = kind === 'task' ? activityTargetProblem(activity) : null;
    if (activityProblem) return { missing: activityProblem };
    let deadline: Record<string, unknown>;
    if (shape === 'due') {
      if (!dueAt) {
        // §1.8: an optional deadline may stay empty - no date, no schedule
        const optional = kind === 'tool' && target && dueOf(preset?.defaults?.[target])?.optional;
        if (!optional) return { missing: 'homework.assign.required.dueAt' };
        deadline = { dueAt: null, schedule: null };
      } else deadline = { dueAt: dueAt.toISOString(), schedule: null };
    } else {
      if (repeatProblem(repeat) === 'WEEKDAY_REQUIRED')
        return { missing: 'homework.assign.required.weekdays' };
      deadline = { dueAt: null, schedule: scheduleOfRepeat(repeat, zone) };
    }
    return {
      input: {
        kind,
        target: targetValue,
        ...titleFields,
        instructions: instructions.trim() || null,
        reminderPolicy: policy,
        activityTarget: kind === 'task' ? activityTargetOf(activity) : null,
        // Q-paper-3 B: a tool's switch rides with the assignment (a worksheet's paper is automatic — the API's)
        ...(kind === 'tool' ? { paperAllowed } : {}),
        ...deadline,
      },
    };
  };

  const submit = async () => {
    setProblem(null);
    setEdited(false);
    const composed = compose();
    if ('missing' in composed) {
      setProblem(composed.missing.includes(' ') ? composed.missing : t(composed.missing));
      return;
    }
    setSaving(true);
    // a client this drawer already assigned is never assigned twice: after a partial result, pressing
    // Assign again retries only the refused ones (the rest keep their outcome)
    const already = outcomes.filter((o) => o.ok);
    const results: AssignOutcome[] = [...already];
    // ONE AssignHomework per client, in order — a refusal for one never stops the others (Q-e34-5)
    for (const c of assignable.filter(
      (x) => !already.some((o) => o.subject === x.subject),
    )) {
      const input = { subject: c.subject, ...composed.input };
      try {
        await assignHomeworkInputSchema.validate(input);
      } catch {
        results.push({
          ...c,
          ok: false,
          message: t('homework.assign.failed'),
        });
        continue;
      }
      const r = await run('assignHomework', input, t('homework.assign.failed'));
      results.push(
        r.ok
          ? { ...c, ok: true, assignmentId: r.assignmentId }
          : { ...c, ok: false, message: r.message },
      );
    }
    setSaving(false);
    setOutcomes(results);
    const landed = results.filter((r) => r.ok).length;
    if (landed > already.length) onAssigned(results);
    if (landed === results.length) {
      message.success(
        results.length > 1
          ? t('homework.assign.doneMany', { count: landed })
          : t('homework.assign.done'),
      );
      onClose();
    }
  };

  const refused = outcomes.filter((o) => !o.ok);
  const many = clients.length > 1;
  // a single client's refusal is about the form as it was pressed; a partial list stays for the retry
  const showRefusal = refused.length > 0 && (many || !edited);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose
      title={
        many
          ? t('homework.assign.titleMany', { count: clients.length })
          : t('homework.assign.title')
      }
      footer={
        <SubmitFooter
          errors={
            <>
              {showRefusal && !many ? (
                <Alert
                  type="error"
                  showIcon
                  message={refused[0].message}
                  data-testid={`homework-assign-refusal-${refused[0].subject}`}
                />
              ) : null}
              {showRefusal && many ? (
                <Alert
                  type="warning"
                  showIcon
                  data-testid="homework-assign-partial"
                  message={t('homework.assign.partial', {
                    done: outcomes.length - refused.length,
                    count: outcomes.length,
                  })}
                  description={
                    <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                      {refused.map((o) => (
                        <li
                          key={o.subject}
                          data-testid={`homework-assign-refusal-${o.subject}`}
                        >
                          <b>
                            <ClientName
                              id={o.subject}
                              name={o.name}
                              visible={namesVisible}
                            />
                          </b>{' '}
                          — {o.message}
                        </li>
                      ))}
                    </ul>
                  }
                />
              ) : null}
              {problem ? (
                <Alert
                  type="error"
                  showIcon
                  message={problem}
                  data-testid="homework-assign-problem"
                />
              ) : null}
            </>
          }
        >
          <Button onClick={onClose} data-testid="homework-assign-cancel">
            {t('homework.assign.cancel')}
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={nobodyToAssign}
            onClick={submit}
            data-testid="homework-assign-submit"
          >
            {t('homework.assign.submit')}
          </Button>
        </SubmitFooter>
      }
    >
      <Space
        direction="vertical"
        size="middle"
        style={{ width: '100%' }}
        data-testid="homework-assign-drawer"
      >
        {many || clients.length === 1 ? (
          <div data-testid="homework-assign-clients">
            <Typography.Text type="secondary">
              {t('homework.assign.forClients')}
            </Typography.Text>
            <div style={{ marginTop: 4 }}>
              {clients.map((c) => {
                const o = outcomes.find((x) => x.subject === c.subject);
                return (
                  <Tag
                    key={c.subject}
                    color={o ? (o.ok ? 'green' : 'red') : undefined}
                    data-testid={`homework-assign-client-${c.subject}`}
                  >
                    <ClientName
                      id={c.subject}
                      name={c.name}
                      visible={namesVisible}
                    />
                  </Tag>
                );
              })}
            </div>
          </div>
        ) : null}

        {clients
          .filter((c) => c.appReady === false)
          .map((c) => (
            <Alert
              key={`app-${c.subject}`}
              type="info"
              showIcon
              message={
                <ClientNameInText
                  text={t('homework.assign.appNotReady', { name: NAME_TOKEN })}
                  id={c.subject}
                  name={c.name}
                  visible={namesVisible}
                />
              }
              data-testid={`homework-assign-app-not-ready-${c.subject}`}
            />
          ))}

        {blocked.map((c) => (
          <Alert
            key={`blocked-${c.subject}`}
            type="error"
            showIcon
            message={
              <ClientNameInText
                text={t('homework.assign.notConnected', { name: NAME_TOKEN })}
                id={c.subject}
                name={c.name}
                visible={namesVisible}
              />
            }
            data-testid={`homework-assign-not-connected-${c.subject}`}
          />
        ))}

        {preset?.targets ? null : (
        <div>
          <Typography.Text>{t('homework.assign.kind')}</Typography.Text>
          <Radio.Group
            style={{ display: 'flex', flexWrap: 'wrap', marginTop: 4 }}
            optionType="button"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setPaperAllowed(false);
              setAllLanguages(false);
              setCategory(ALL_CATEGORIES);
              setTarget(undefined);
              setLink('');
              setActivity(freshActivityTarget());
            }}
            data-testid="homework-assign-kind"
          >
            {ASSIGNABLE_KINDS.filter((k) => !kindHidden(k)).map((k) => (
              <Radio.Button
                key={k}
                value={k}
                data-testid={`homework-assign-kind-${k}`}
              >
                {t(`homework.kind.${k}`)}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
        )}

        <div>
          <Typography.Text>{t('homework.assign.target')}</Typography.Text>
          {kind === 'task' ? (
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              {t('homework.assign.taskNoTarget')}
            </Typography.Paragraph>
          ) : kind === 'video' ? (
            <VideoTargetInput
              value={link || null}
              onChange={(v, suggested) => {
                setLink(v ?? '');
                // a Learn clip's name is the title's suggestion (still editable), as a worksheet's is
                if (suggested && !title.trim()) setTitle(suggested);
              }}
              testId="homework-assign-video"
              clientLng={clients[0]?.lng}
            />
          ) : kind === 'link' ? (
            <Input
              value={link}
              placeholder={t('homework.assign.linkPlaceholder')}
              onChange={(e) => setLink(e.target.value)}
              data-testid="homework-assign-link"
            />
          ) : (
            <>
              {kind === 'worksheet' ? (
                <div style={{ marginBottom: 4 }}>
                  <WorksheetCategorySelect
                    templates={worksheets}
                    value={category}
                    onChange={(c) => {
                      setCategory(c);
                      // Q-wcat-2 A: a chosen worksheet outside the new category clears
                      setTarget(keepIfInCategory(target, worksheets, c));
                    }}
                    data-testid="homework-assign-category"
                  />
                </div>
              ) : null}
            {kind === 'tool' && preset?.targets && preset.targets.length <= 4 ? (
              <Radio.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}
                value={target}
                onChange={(e) => {
                  setTarget(e.target.value);
                  applyDefaults(preset.defaults?.[e.target.value], false);
                }}
                data-testid="homework-assign-target"
              >
                {preset.targets
                  .filter((x) => !toolTargetHidden(x))
                  .map((x) => (
                    <Radio key={x} value={x} data-testid={`homework-assign-target-${x}`}>
                      {t(`homework.target.tool.${x}`)}
                    </Radio>
                  ))}
              </Radio.Group>
            ) : (
            <Select
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              filterOption={
                kind === 'tool'
                  ? (input, option) =>
                      `${option?.label ?? ''} ${(option as { group?: string } | undefined)?.group ?? ''}`
                        .toLowerCase()
                        .includes(input.trim().toLowerCase())
                  : undefined
              }
              value={target}
              placeholder={t('homework.assign.targetPlaceholder')}
              onChange={(v: string) => {
                setTarget(v);
                // §1.8: a tool preset's target brings its own defaults (and a fresh ref and count)
                if (kind === 'tool') applyDefaults(preset?.defaults?.[v], false);
                // a worksheet's title defaults to the template's name (still editable)
                if (kind === 'worksheet' && !title.trim()) {
                  const w = worksheets.find((x) => x.templateId === v);
                  if (w) setTitle(w.name);
                }
              }}
              options={targetOptions}
              data-testid="homework-assign-target"
            />
            )}
            </>
          )}
          {kind === 'worksheet' ? (
            <Space size={8} style={{ marginTop: 4 }}>
              <Switch
                size="small"
                checked={allLanguages}
                onChange={(on) => {
                  setAllLanguages(on);
                  setCategory(ALL_CATEGORIES);
                  setTarget(undefined); // the list changes: a pick from the other list no longer stands
                }}
                data-testid="homework-assign-target-all-languages"
              />
              <Typography.Text style={{ fontSize: 12 }}>
                {t('worksheets.library.allLanguages')}
              </Typography.Text>
            </Space>
          ) : null}
          {kind === 'tool' && target && preset?.defaults?.[target]?.ref ? (
            <div style={{ marginTop: 8 }}>
              <Typography.Text>{preset.defaults[target]?.ref?.label}</Typography.Text>
              <Select
                style={{ width: '100%' }}
                value={ref}
                placeholder={t('homework.assign.targetPlaceholder')}
                onChange={(v: string) => {
                  setRef(v);
                  // the title follows the chosen ref while the therapist has not typed it
                  const d = preset.defaults?.[target];
                  const label = d?.ref?.options.find((o) => o.value === v)?.label ?? null;
                  const next = defaultTitleOf(d, d?.ref?.any?.value === v ? null : label);
                  if (next !== null && !titleTouched) setTitle(next);
                }}
                options={[
                  ...(preset.defaults[target]?.ref?.any ? [preset.defaults[target]!.ref!.any!] : []),
                  ...(preset.defaults[target]?.ref?.options ?? []),
                ]}
                data-testid="homework-assign-ref"
              />
            </div>
          ) : null}
          {kind === 'tool' && target === 'diaryCard' ? (
            <div style={{ marginTop: 8 }}>
              <Typography.Text>{t('homework.assign.diaryTemplate')}</Typography.Text>
              <Select
                allowClear
                style={{ width: '100%' }}
                value={diaryTemplate}
                placeholder={t('homework.assign.diaryTemplatePlaceholder')}
                onChange={(v: string | undefined) => setDiaryTemplate(v)}
                options={diaryTemplates.map((x) => ({ value: x.templateId, label: x.name }))}
                data-testid="homework-assign-diary-template"
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('homework.assign.diaryTemplateHelp')}
              </Typography.Text>
            </div>
          ) : null}
          {kind !== 'task' && kind !== 'video' ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t(`homework.assign.targetHelp.${kind}`)}
            </Typography.Text>
          ) : null}
          {kind === 'tool' ? (
            // Q-paper-3 B: the therapist turns paper on per assignment; off by default
            <div style={{ marginTop: 8 }}>
              <Space size={8} align="start">
                <Switch
                  size="small"
                  checked={paperAllowed}
                  onChange={setPaperAllowed}
                  data-testid="homework-assign-paper-allowed"
                />
                <Typography.Text>{t('homework.assign.paperAllowed')}</Typography.Text>
              </Space>
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, margin: '2px 0 0' }}
                data-testid="homework-assign-paper-help"
              >
                {t('homework.assign.paperAllowedHelp')}
              </Typography.Paragraph>
            </div>
          ) : null}
          {kind === 'worksheet' && target ? (
            // Q-paper-2 A: automatic by the template (unscored and not a consent → paper) — no switch
            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 12, margin: '4px 0 0' }}
              data-testid="homework-assign-paper-worksheet"
            >
              {t(
                worksheets.find((w) => w.templateId === target)?.paperEligible
                  ? 'homework.assign.paperWorksheet.yes'
                  : 'homework.assign.paperWorksheet.no',
              )}
            </Typography.Paragraph>
          ) : null}
          <Typography.Paragraph
            type="secondary"
            style={{ fontSize: 12, margin: '4px 0 0' }}
            data-testid="homework-assign-completes"
          >
            {t(`homework.assign.completes.${kind}`)}
          </Typography.Paragraph>
        </div>

        {kind === 'task' ? (
          <div data-testid="homework-assign-activity">
            <Space size={8} align="start">
              <Switch
                size="small"
                checked={activity.on}
                onChange={(on) => setActivity({ ...activity, on })}
                data-testid="homework-assign-activity-on"
              />
              <Typography.Text>{t('homework.assign.activity.switch')}</Typography.Text>
            </Space>
            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 12, margin: '2px 0 0' }}
            >
              {t('homework.assign.activity.help')}
            </Typography.Paragraph>
            {activity.on ? (
              <Space wrap align="end" style={{ marginTop: 8 }}>
                <div>
                  <Typography.Text>{t('homework.assign.activity.type')}</Typography.Text>
                  <Select
                    style={{ width: 180, display: 'block' }}
                    value={activity.workoutType}
                    onChange={(v: string) => setActivity({ ...activity, workoutType: v })}
                    options={[
                      { value: ANY_WORKOUT, label: t('homework.assign.activity.anyWorkout') },
                      ...WORKOUT_TYPES.map((w) => ({
                        value: w,
                        label: t(`homework.assign.activity.workout.${w}`),
                      })),
                    ]}
                    data-testid="homework-assign-activity-type"
                  />
                </div>
                <div>
                  <Typography.Text>{t('homework.assign.activity.minutesEach')}</Typography.Text>
                  <Input
                    style={{ width: 110, display: 'block' }}
                    inputMode="numeric"
                    maxLength={3}
                    value={activity.minutesEach}
                    placeholder={`1–${HOMEWORK_ACTIVITY_MINUTES_MAX}`}
                    onChange={(e) => setActivity({ ...activity, minutesEach: e.target.value })}
                    data-testid="homework-assign-activity-minutes"
                  />
                </div>
                <div>
                  <Typography.Text>{t('homework.assign.activity.times')}</Typography.Text>
                  <Input
                    style={{ width: 110, display: 'block' }}
                    inputMode="numeric"
                    maxLength={1}
                    value={activity.times}
                    placeholder={`1–${HOMEWORK_ACTIVITY_TIMES_MAX}`}
                    onChange={(e) => setActivity({ ...activity, times: e.target.value })}
                    data-testid="homework-assign-activity-times"
                  />
                </div>
              </Space>
            ) : null}
            {activity.on && !activityTargetProblem(activity) ? (
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, margin: '4px 0 0' }}
                data-testid="homework-assign-activity-summary"
              >
                {t('homework.assign.activity.summary', {
                  type:
                    activity.workoutType === ANY_WORKOUT
                      ? t('homework.assign.activity.anyWorkout')
                      : t(`homework.assign.activity.workout.${activity.workoutType}`),
                  minutes: activity.minutesEach.trim(),
                  times: activity.times.trim(),
                })}
              </Typography.Paragraph>
            ) : null}
          </div>
        ) : null}

        <div>
          <Typography.Text>{t('homework.assign.titleLabel')}</Typography.Text>
          <Input
            value={title}
            maxLength={200}
            placeholder={titlePlaceholder}
            onChange={(e) => {
              setTitleTouched(true);
              setTitle(e.target.value);
            }}
            data-testid="homework-assign-title"
          />
        </div>

        <div>
          <Typography.Text>{t('homework.assign.instructions')}</Typography.Text>
          <Input.TextArea
            value={instructions}
            maxLength={4000}
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={instructionsPlaceholder}
            onChange={(e) => {
              setInstructions(e.target.value);
            }}
            data-testid="homework-assign-instructions"
          />
        </div>

        <div>
          <Typography.Text>{t('homework.assign.deadline')}</Typography.Text>
          <Radio.Group
            style={{ display: 'flex', marginTop: 4 }}
            value={shape}
            onChange={(e) => setShape(e.target.value)}
            data-testid="homework-assign-deadline"
          >
            <Radio value="due" data-testid="homework-assign-deadline-due">
              {t('homework.assign.deadlineDue')}
            </Radio>
            <Radio value="repeat" data-testid="homework-assign-deadline-repeat">
              {t('homework.assign.deadlineRepeat')}
            </Radio>
          </Radio.Group>
        </div>

        {shape === 'due' ? (
          <div>
            <Typography.Text>{t('homework.assign.dueAt')}</Typography.Text>
            <DateFnsDatePicker
              locale={pickerLocale}
              style={{ width: '100%' }}
              showTime={{
                format: 'HH:mm',
                minuteStep: 5,
                defaultValue: dueDefaultTime,
              }}
              format="yyyy-MM-dd HH:mm"
              value={dueAt}
              disabledDate={(d: Date) => d < startOfDay(new Date())}
              onChange={(d: Date | null) => setDueAt(d)}
              data-testid="homework-assign-due"
            />
          </div>
        ) : (
          <RepeatEditor
            value={repeat}
            onChange={setRepeat}
            testPrefix="homework-assign"
            count={repeatDefaults ? { max: repeatDefaults.countMax, unit: repeatDefaults.countUnit } : undefined}
          />
        )}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {zoneNote}
        </Typography.Text>

        <div>
          <Typography.Text>
            {t('homework.assign.reminderPolicy')}
          </Typography.Text>
          <Select
            style={{ width: '100%' }}
            value={policy}
            onChange={setPolicy}
            options={REMINDER_POLICIES.map((p) => ({
              value: p,
              label: t(`homework.reminderPolicy.${p}`),
            }))}
            data-testid="homework-assign-reminder-policy"
          />
        </div>
      </Space>
    </Drawer>
  );
};
